import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Fragment, type FC, useEffect, useRef, useState } from 'react';

import {
  CAMERA_ZONE_TYPES,
  type CameraZone,
  type CameraZonePoint,
  type CameraZoneType,
  StreamQuality,
  useStreamData,
  useUpdateCameraZones,
} from '../../api/index.js';
import VideoJs from '../VideoJs.js';

interface Props {
  open: boolean;
  cameraId: string;
  cameraName: string;
  initialZones?: CameraZone[] | null;
  onClose: () => void;
}

type Drag =
  | { kind: 'create'; start: CameraZonePoint }
  | { kind: 'move-corner'; zoneId: string; cornerIdx: number };

const MIN_SIZE = 0.02;
const newId = () => Math.random().toString(36).slice(2, 10);

function rectCorners(a: CameraZonePoint, b: CameraZonePoint): CameraZonePoint[] {
  const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x);
  const y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
  return [{ x: x1, y: y1 }, { x: x2, y: y1 }, { x: x2, y: y2 }, { x: x1, y: y2 }];
}

function pointsAttr(corners: CameraZonePoint[]): string {
  return corners.map((c) => `${c.x * 100},${c.y * 100}`).join(' ');
}

function sanitizeZones(zones: CameraZone[] | null | undefined): CameraZone[] {
  return (zones ?? []).filter(
    (z): z is CameraZone => Array.isArray(z?.corners) && z.corners.length === 4,
  );
}

function bbox(corners: CameraZonePoint[]): { x1: number; y1: number; x2: number; y2: number } {
  const xs = corners.map((c) => c.x), ys = corners.map((c) => c.y);
  return { x1: Math.min(...xs), y1: Math.min(...ys), x2: Math.max(...xs), y2: Math.max(...ys) };
}

const CameraZoneEditor: FC<Props> = ({ open, cameraId, cameraName, initialZones, onClose }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;
  const [zones, setZones] = useState<CameraZone[]>(sanitizeZones(initialZones));
  const [selected, setSelected] = useState<string | null>(null);
  const [drag, setDrag] = useState<Drag | null>(null);
  const [draftEnd, setDraftEnd] = useState<CameraZonePoint | null>(null);
  const surfaceRef = useRef<HTMLDivElement | null>(null);
  const stream = useStreamData(cameraId, StreamQuality.LOW, { enabled: open });
  const save = useUpdateCameraZones(cameraId);

  useEffect(() => { setZones(sanitizeZones(initialZones)); setSelected(null); }, [initialZones, open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      const editing = t?.tagName === 'INPUT' || t?.tagName === 'TEXTAREA' || t?.isContentEditable;
      if (e.key === 'Escape') { setDrag(null); setDraftEnd(null); setSelected(null); }
      if ((e.key === 'Delete' || e.key === 'Backspace') && selected && !editing) {
        setZones((zs) => zs.filter((z) => z.id !== selected));
        setSelected(null);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, selected]);

  const toNorm = (clientX: number, clientY: number): CameraZonePoint | null => {
    const rect = surfaceRef.current?.getBoundingClientRect();
    if (!rect || rect.width === 0 || rect.height === 0) return null;
    return {
      x: Math.min(1, Math.max(0, (clientX - rect.left) / rect.width)),
      y: Math.min(1, Math.max(0, (clientY - rect.top) / rect.height)),
    };
  };

  const onSurfaceDown = (e: React.MouseEvent) => {
    const p = toNorm(e.clientX, e.clientY); if (!p) return;
    setDrag({ kind: 'create', start: p });
    setDraftEnd(p);
    setSelected(null);
  };

  const onSurfaceMove = (e: React.MouseEvent) => {
    const p = toNorm(e.clientX, e.clientY); if (!p || !drag) return;
    if (drag.kind === 'create') {
      setDraftEnd(p);
      return;
    }
    setZones((zs) => zs.map((z) => z.id === drag.zoneId
      ? { ...z, corners: z.corners.map((c, i) => i === drag.cornerIdx ? p : c) }
      : z));
  };

  const onSurfaceUp = () => {
    if (drag?.kind === 'create' && draftEnd) {
      const w = Math.abs(draftEnd.x - drag.start.x);
      const h = Math.abs(draftEnd.y - drag.start.y);
      if (w >= MIN_SIZE && h >= MIN_SIZE) {
        const id = newId();
        setZones((zs) => [...zs, { id, label: 'unlabeled', corners: rectCorners(drag.start, draftEnd) }]);
        setSelected(id);
      }
    }
    setDrag(null);
    setDraftEnd(null);
  };

  const onCornerDown = (e: React.MouseEvent, zoneId: string, cornerIdx: number) => {
    e.stopPropagation();
    setSelected(zoneId);
    setDrag({ kind: 'move-corner', zoneId, cornerIdx });
  };

  const onPolygonDown = (e: React.MouseEvent, zoneId: string) => {
    e.stopPropagation();
    setSelected(zoneId);
  };

  const submit = () => save.mutate(zones, { onSuccess: onClose });

  const draftCorners = drag?.kind === 'create' && draftEnd ? rectCorners(drag.start, draftEnd) : null;

  return (
    <Dialog open={open} onClose={onClose} fullScreen>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
        <Box>
          <Typography variant="overline" color="text.secondary" sx={{ lineHeight: 1, display: 'block' }}>
            Zone editor · live
          </Typography>
          <Typography variant="h6" sx={{ lineHeight: 1.2 }}>{cameraName}</Typography>
        </Box>
        <Box sx={{ flex: 1 }} />
        <IconButton onClick={onClose} edge="end" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers sx={{ p: 0, display: 'flex', minHeight: 0 }}>
        <Box sx={{ flex: 1, bgcolor: 'grey.900', display: 'grid', placeItems: 'center', overflow: 'hidden', p: 2 }}>
          {stream.isLoading && (
            <Stack alignItems="center" spacing={1.5} sx={{ color: 'grey.400' }}>
              <CircularProgress size={28} />
              <Typography variant="caption">Connecting to broadcast…</Typography>
            </Stack>
          )}
          {stream.isError && (
            <Typography variant="body2" color="error.light" sx={{ textAlign: 'center', maxWidth: 320 }}>
              Stream unavailable. Make sure the low-quality broadcast is running for this camera.
            </Typography>
          )}
          {stream.data && (
            <Box
              ref={surfaceRef}
              onMouseDown={onSurfaceDown}
              onMouseMove={onSurfaceMove}
              onMouseUp={onSurfaceUp}
              sx={{
                position: 'relative',
                width: '100%',
                maxWidth: 'calc((100vh - 200px) * 16 / 9)',
                aspectRatio: '16 / 9',
                cursor: 'crosshair',
                userSelect: 'none',
                boxShadow: 3,
              }}
            >
              <Box sx={{ position: 'absolute', inset: 0, '& .video-js': { width: '100%', height: '100%' }, '& video': { objectFit: 'cover' } }}>
                <VideoJs
                  live
                  streamId={stream.data.streamId}
                  token={stream.data.token}
                  options={{ autoplay: true, muted: true, controls: false, fluid: false, fill: true }}
                  src={stream.data.hlsUrl}
                />
              </Box>
              <svg
                width="100%" height="100%"
                viewBox="0 0 100 100" preserveAspectRatio="none"
                style={{ position: 'absolute', inset: 0, pointerEvents: 'none' }}
              >
                {zones.map((z) => (
                  <Fragment key={z.id}>
                    <polygon
                      points={pointsAttr(z.corners)}
                      fill="none" stroke="rgba(0,0,0,0.55)"
                      strokeWidth={selected === z.id ? 5 : 4}
                      vectorEffect="non-scaling-stroke"
                    />
                    <polygon
                      points={pointsAttr(z.corners)}
                      fill={`${primary}${selected === z.id ? '66' : '40'}`}
                      stroke={primary}
                      strokeWidth={selected === z.id ? 3 : 2}
                      vectorEffect="non-scaling-stroke"
                      style={{ pointerEvents: 'all', cursor: 'pointer' }}
                      onMouseDown={(e) => onPolygonDown(e, z.id)}
                    />
                  </Fragment>
                ))}
                {draftCorners && (
                  <polygon
                    points={pointsAttr(draftCorners)}
                    fill={`${primary}66`} stroke={primary} strokeWidth={2}
                    strokeDasharray="3 2" vectorEffect="non-scaling-stroke"
                  />
                )}
              </svg>
              {zones.map((z) => (
                <Fragment key={z.id}>
                  <Box sx={{
                    position: 'absolute',
                    left: `${z.corners[0]!.x * 100}%`,
                    top: `${z.corners[0]!.y * 100}%`,
                    transform: 'translateY(-100%)',
                    bgcolor: primary, color: 'common.white', fontSize: 11, fontWeight: 500,
                    px: 0.75, py: 0.25, borderRadius: 0.5, whiteSpace: 'nowrap', lineHeight: 1.4,
                    pointerEvents: 'none',
                  }}>
                    {z.label}
                  </Box>
                  {selected === z.id && z.corners.map((c, i) => (
                    <Box
                      key={i}
                      onMouseDown={(e) => onCornerDown(e, z.id, i)}
                      sx={{
                        position: 'absolute',
                        left: `${c.x * 100}%`, top: `${c.y * 100}%`,
                        width: 14, height: 14,
                        transform: 'translate(-50%, -50%)',
                        bgcolor: 'common.white', border: `2px solid ${primary}`, borderRadius: '50%',
                        cursor: 'move', pointerEvents: 'auto', boxSizing: 'border-box',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.4)',
                      }}
                    />
                  ))}
                </Fragment>
              ))}
            </Box>
          )}
        </Box>

        <Stack sx={{ width: 320, borderLeft: 1, borderColor: 'divider', minHeight: 0 }}>
          <Box sx={{ px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="overline" color="text.secondary">
              Zones · {zones.length}
            </Typography>
          </Box>
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {zones.length === 0 ? (
              <Box sx={{ p: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Drag on the live feed to draw a zone. Label it like <strong>bathroom door</strong> or <strong>bed</strong> so the AI can use it as spatial context. After it appears, drag any corner to shape the outline however you need.
                </Typography>
              </Box>
            ) : zones.map((z, i) => (
              <ZoneRow
                key={z.id} index={i} zone={z} selected={selected === z.id}
                onSelect={() => setSelected(z.id)}
                onChange={(patch) => setZones((zs) => zs.map((x) => x.id === z.id ? { ...x, ...patch } : x))}
                onDelete={() => { setZones((zs) => zs.filter((x) => x.id !== z.id)); if (selected === z.id) setSelected(null); }}
              />
            ))}
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
          Drag empty space to draw · click to select · drag corners to reshape · Esc cancels · Del removes
        </Typography>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={submit} disabled={save.isLoading} variant="contained">
          {save.isLoading ? <CircularProgress size={16} /> : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const ZoneRow: FC<{
  index: number; zone: CameraZone; selected: boolean;
  onSelect: () => void; onChange: (patch: Partial<CameraZone>) => void; onDelete: () => void;
}> = ({ index, zone, selected, onSelect, onChange, onDelete }) => {
  const b = bbox(zone.corners);
  return (
    <Stack
      onClick={onSelect}
      sx={{
        px: 2, py: 1.25, borderBottom: 1, borderColor: 'divider', cursor: 'pointer',
        bgcolor: selected ? 'action.selected' : 'transparent',
        borderLeft: 3, borderLeftColor: selected ? 'primary.main' : 'transparent',
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography variant="caption" color="text.secondary" sx={{ width: 18 }}>{index + 1}</Typography>
        <TextField
          value={zone.label}
          onChange={(e) => onChange({ label: e.target.value.slice(0, 40) })}
          onClick={(e) => e.stopPropagation()}
          inputProps={{ maxLength: 40 }}
          variant="standard"
          size="small"
          sx={{ flex: 1 }}
        />
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(); }} aria-label="Delete zone">
          <DeleteOutlineIcon fontSize="small" />
        </IconButton>
      </Stack>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ ml: 3.25, mt: 0.5 }}>
        <TextField
          select
          value={zone.type ?? ''}
          onChange={(e) => onChange({ type: (e.target.value || null) as CameraZoneType | null })}
          onClick={(e) => e.stopPropagation()}
          variant="standard"
          size="small"
          label="Type"
          sx={{ minWidth: 120 }}
        >
          <MenuItem value="">
            <em>From label</em>
          </MenuItem>
          {CAMERA_ZONE_TYPES.map((zoneType) => (
            <MenuItem key={zoneType} value={zoneType}>
              {zoneType}
            </MenuItem>
          ))}
        </TextField>
        <Typography variant="caption" color="text.secondary">
          bbox {Math.round((b.x2 - b.x1) * 100)}×{Math.round((b.y2 - b.y1) * 100)}%
        </Typography>
      </Stack>
    </Stack>
  );
};

export default CameraZoneEditor;
